import React, { useState } from "react";
import { useAssessmentTemplates, useReviewLibrary } from "../../hooks/useAssessmentTemplates";
import { Button, Alert, EmptyState, Field, Modal, TopbarSlot } from "../../components";
import LibraryItemDrawer from "./LibraryItemDrawer";
import TemplateBuilder from "./TemplateBuilder";
import "./Reviews.css";

const ITEMS_PER_CATEGORY = 9;

// "1 categories" reads as a bug to anyone looking at it, and these counts are usually
// small enough for the singular to come up often.
const plural = (n, one, many) => `${n} ${n === 1 ? one : many || `${one}s`}`;

// Assessment templates, and the item library they draw from.
//
// Distinct from Report Templates, which control report layout: these control what an
// assessment asks. The kit makes the same distinction on screen.
const ReviewTemplatesScreen = ({ isAdmin }) => {
  const { templates, loading, error, create, publish, update, remove } = useAssessmentTemplates();
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const { items, loading: libLoading, reload: reloadLibrary, remove: removeItem } = useReviewLibrary();
  const [tab, setTab] = useState("templates");
  const [composing, setComposing] = useState(false);
  const [notice, setNotice] = useState("");
  const [openItemId, setOpenItemId] = useState(null);
  const [creatingItem, setCreatingItem] = useState(false);
  const [buildingId, setBuildingId] = useState(null);
  const [failure, setFailure] = useState("");

  const onPublish = async (template) => {
    setNotice(""); setFailure("");
    const result = await publish(template.id);
    if (result.ok) {
      setNotice(`${template.name} published as version ${result.version}.`);
    } else {
      const short = (result.incomplete || [])
        .map((c) => `${c.category} has ${c.count}`)
        .join(", ");
      setFailure(short ? `${result.error}: ${short}.` : result.error);
    }
  };

  if (buildingId) return <TemplateBuilder id={buildingId} onBack={() => setBuildingId(null)} />;

  return (
    <div className="v2-page">
      {isAdmin && (
        <TopbarSlot>
          {tab === "templates"
            ? <Button variant="primary" onClick={() => setComposing(true)}>New template</Button>
            : <Button variant="primary" onClick={() => setCreatingItem(true)}>New item</Button>}
        </TopbarSlot>
      )}

      <div className="v2-pagehead">
        <div>
          <h2>Assessment templates</h2>
          <p>What an assessment asks. Distinct from report templates, which control layout.</p>
        </div>
      </div>

      <div className="v2-views">
        <button type="button" className={`v2-view${tab === "templates" ? " on" : ""}`}
                onClick={() => setTab("templates")}>
          Templates<span className="n">{templates.length}</span>
        </button>
        <button type="button" className={`v2-view${tab === "library" ? " on" : ""}`}
                onClick={() => setTab("library")}>
          Item library<span className="n">{items.length}</span>
        </button>
      </div>

      <Alert>{error || failure}</Alert>
      {notice && <Alert tone="success">{notice}</Alert>}

      {tab === "templates" ? (
        <>
          <div className="v2-rule-callout" style={{ margin: ".875rem 0" }}>
            <div>
              <b>Every active category holds exactly {ITEMS_PER_CATEGORY} items.</b>
              <span>A template cannot be published until each enabled category is full.</span>
            </div>
          </div>

          {loading ? <p className="v2-hint">Loading…</p>
            : templates.length === 0 ? (
              <EmptyState title="No templates yet"
                action={isAdmin
                  ? <Button variant="primary" style={{ marginTop: ".75rem" }} onClick={() => setComposing(true)}>New template</Button>
                  : null}>
                A template is the set of items every assessment starts from.
              </EmptyState>
            ) : (
              <div className="v2-tpl-grid">
                {templates.map((t) => (
                  <div key={t.id} className="v2-tpl-card v2-tpl-card--clickable"
                       role={isAdmin ? "button" : undefined}
                       tabIndex={isAdmin ? 0 : undefined}
                       onClick={() => isAdmin && setBuildingId(t.id)}
                       onKeyDown={(e) => {
                         if (isAdmin && (e.key === "Enter" || e.key === " ")) {
                           e.preventDefault(); setBuildingId(t.id);
                         }
                       }}>
                    <div>
                      <span className={`v2-rbadge${t.state === "published" ? " v2-rbadge--success" : ""}`}>
                        {t.has_unpublished_changes && t.current_version > 0
                          ? "PUBLISHED · DRAFT CHANGES"
                          : t.state.toUpperCase()}
                      </span>
                      {/* A shared starter template. Every workspace sees the same row, so
                          nobody may rename or unpublish it — the server refuses too. */}
                      {t.tenant_id === null && (
                        <span className="v2-rbadge v2-tpl-default" title="Shared with every workspace">
                          DEFAULT
                        </span>
                      )}
                    </div>
                    <div className="v2-tpl-card-name">{t.name}</div>
                    {t.description && <div className="v2-tpl-card-desc">{t.description}</div>}
                    {t.recommended_cadence && (
                      <div className="v2-tpl-card-desc" title="How often this template is designed to be run">
                        <b>Recommended:</b> {t.recommended_cadence}
                      </div>
                    )}
                    <div className="v2-tpl-dots">
                      {(t.categories || []).map((c) => (
                        <span key={c.name}
                              title={`${c.name}: ${c.count} of ${ITEMS_PER_CATEGORY}`}
                              className={`v2-tpl-dot ${c.count === ITEMS_PER_CATEGORY ? "full" : c.count > 0 ? "part" : ""}`} />
                      ))}
                    </div>
                    <div className="v2-tpl-card-meta">
                      {plural(t.category_count, "category", "categories")} ·{" "}
                      {plural(t.item_count, "item")} · {plural(t.uses, "use")}
                    </div>
                    <div className="v2-tpl-card-meta">
                      {t.current_version > 0 ? `Version ${t.current_version}` : "Never published"}
                    </div>
                    {isAdmin && t.tenant_id !== null && (
                      <div className="v2-tpl-card-actions">
                        <Button onClick={(e) => { e.stopPropagation(); setEditing(t); }}>Edit</Button>
                        <Button onClick={(e) => {
                          e.stopPropagation();
                          setConfirm({
                            title: `Delete ${t.name}?`,
                            body: t.uses > 0
                              ? `${t.name} has been used by ${t.uses} assessment${t.uses === 1 ? "" : "s"}. Those assessments keep their own copy of the wording, but the template itself is part of their history and cannot be removed.`
                              : "This removes the template and its published versions. Assessments already created from it are unaffected.",
                            blocked: t.uses > 0,
                            onConfirm: () => remove(t.id),
                          });
                        }}>Delete</Button>
                        <Button onClick={(e) => { e.stopPropagation(); onPublish(t); }}
                                disabled={t.state === "published" && !t.has_unpublished_changes}>
                          {t.current_version > 0 ? "Publish changes" : "Publish"}
                        </Button>
                      </div>
                    )}
                    {t.tenant_id === null && (
                      <div className="v2-tpl-card-meta">
                        Shared default · use it as-is, or build your own
                      </div>
                    )}
                  </div>
                ))}

                {isAdmin && (
                  <button type="button" className="v2-tpl-card v2-tpl-card--new"
                          onClick={() => setComposing(true)}>
                    <span aria-hidden="true">+</span>
                    Start from scratch
                  </button>
                )}
              </div>
            )}
        </>
      ) : (
        libLoading ? <p className="v2-hint">Loading…</p>
        : items.length === 0 ? (
          <EmptyState title="The library is empty"
            action={isAdmin
              ? <Button variant="primary" style={{ marginTop: ".75rem" }} onClick={() => setCreatingItem(true)}>New item</Button>
              : null}>
            Each item carries three pre-written statements, one per rating, so the same
            finding reads the same way for every client. Templates are built from these.
          </EmptyState>
        ) : (
          <div className="v2-tablewrap" style={{ marginTop: ".875rem" }}>
            <table className="v2-table">
              <thead>
                <tr>
                  <th>Assessment item</th><th>Categories</th>
                  <th className="v2-num">Weight</th>
                  <th className="v2-num">Statements</th><th>Status</th>
                  {isAdmin && <th style={{ width: 70 }} />}
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id} onClick={() => setOpenItemId(i.id)} style={{ cursor: "pointer" }}>
                    <td>
                      <button type="button" className="v2-linkcell">{i.name}</button>
                      {/* Same badge the template cards use, for the same reason: a row
                          with no Delete needs to say why, not just lack the control. */}
                      {i.is_default && (
                        <span className="v2-rbadge v2-tpl-default" title="Shared with every workspace">
                          DEFAULT
                        </span>
                      )}
                      {i.question && <div className="v2-tpl-card-desc">{i.question}</div>}
                    </td>
                    <td>
                      {i.categories.length === 0 ? <span className="v2-dim">—</span>
                        : i.categories.map((c) => <span key={c} className="v2-rbadge">{c}</span>)}
                    </td>
                    <td className="v2-num">{i.default_weight}/5</td>
                    <td className="v2-num">
                      {["red", "yellow", "green"].filter((r) => (i.statements?.[r]?.summary || "").trim()).length}
                      /3
                    </td>
                    <td>
                      <span className={`v2-rbadge${i.enabled ? " v2-rbadge--success" : ""}`}>
                        {i.enabled ? "ENABLED" : "DISABLED"}
                      </span>
                    </td>
                    {isAdmin && (
                      <td>
                        {/* Only this workspace's own items. A shared default has no owner
                            to delete it, and the server refuses regardless. */}
                        {i.tenant_id !== null && (
                          <button type="button" className="v2-linkcell v2-linkcell--danger"
                                  aria-label={`Delete ${i.name}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setConfirm({
                                      title: `Delete ${i.name}?`,
                                      body: "If a template still uses this item you will be told which, and nothing is removed. Assessments already created keep their own copy of the wording.",
                                      onConfirm: () => removeItem(i.id),
                                    });
                                  }}>
                            Delete
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {confirm && (
        <ConfirmDeleteModal {...confirm} onClose={() => setConfirm(null)} />
      )}

      {editing && (
        <EditTemplateModal template={editing} onClose={() => setEditing(null)} onSave={update} />
      )}

      {composing && (
        <NewTemplateModal
          onClose={() => setComposing(false)}
          onCreate={async (fields) => {
            const created = await create(fields);
            // Straight into the builder: a template with no categories cannot be
            // published, so stopping at the card would leave a dead end.
            if (created?.id) setBuildingId(created.id);
          }}
        />
      )}

      {(openItemId || creatingItem) && (
        <LibraryItemDrawer
          item={creatingItem
            ? { name: "", question: "", categories: [], default_weight: 3, default_infographic: "none" }
            : items.find((i) => i.id === openItemId)}
          onClose={() => { setOpenItemId(null); setCreatingItem(false); }}
          onSaved={reloadLibrary}
        />
      )}
    </div>
  );
};

const NewTemplateModal = ({ onClose, onCreate }) => {
  const [form, setForm] = useState({ name: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!form.name.trim()) { setErr("Give the template a name."); return; }
    setBusy(true); setErr("");
    try { await onCreate(form); onClose(); }
    catch (e) { setErr(e.message || "Could not create the template."); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="New assessment template" onClose={onClose}
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>{busy ? "Creating…" : "Create"}</Button>
      </>}>
      <Alert>{err}</Alert>
      <Field label="Name">
        <input className="v2-input" value={form.name} autoFocus
               onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
      </Field>
      <Field label="Description" hint="What this template is for, in one line.">
        <input className="v2-input" value={form.description}
               onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
      </Field>
    </Modal>
  );
};


// Name, description and whether the template is on offer. Deliberately separate from the
// builder: renaming a template and rebuilding its contents are different intentions, and
// the builder's save carries the whole body — a rename sent through it would empty the
// template, because parseContent turns a missing body into `{ categories: [] }`.
const EditTemplateModal = ({ template, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: template.name || "",
    description: template.description || "",
    recommended_cadence: template.recommended_cadence || "",
    state: template.state || "draft",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim()) { setErr("Give the template a name."); return; }
    setBusy(true); setErr("");

    // State only ever moves toward draft here. Publishing runs the nine-items-per-category
    // check behind the Publish action; a rename must not become a way around it.
    const patch = {
      name: form.name.trim(),
      description: form.description,
      recommended_cadence: form.recommended_cadence,
    };
    if (form.state === "draft" && template.state === "published") patch.state = "draft";

    const res = await onSave(template.id, patch);
    setBusy(false);
    if (res.ok) onClose(); else setErr(res.error || "Could not save that.");
  };

  return (
    <Modal title={`Edit ${template.name}`} onClose={onClose}
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>{busy ? "Saving…" : "Save"}</Button>
      </>}>
      <Alert>{err}</Alert>

      <Field label="Name">
        <input className="v2-input" value={form.name} onChange={set("name")} autoFocus />
      </Field>
      <Field label="Description">
        <input className="v2-input" value={form.description} onChange={set("description")} />
      </Field>
      <Field label="Recommended cadence"
             hint="Advice shown on the template, such as Quarterly or Semiannual. Each client's actual schedule is set on the review schedule.">
        <input className="v2-input" value={form.recommended_cadence} maxLength={64}
               onChange={set("recommended_cadence")} placeholder="Quarterly" />
      </Field>

      <Field label="Status"
             hint={template.current_version > 0
               ? "Draft keeps the published version and its history, but stops the template being offered for new assessments."
               : "This template has never been published, so it cannot be offered yet."}>
        <select className="v2-input" value={form.state}
                disabled={template.current_version === 0}
                onChange={set("state")}>
          <option value="draft">Draft</option>
          <option value="published" disabled={template.state !== "published"}>Published</option>
        </select>
      </Field>

      {template.state === "published" && form.state === "draft" && (
        <Alert tone="warning">
          This stops the template appearing as a starting point for new assessments.
          Assessments already created from it are unaffected.
        </Alert>
      )}
    </Modal>
  );
};


// One dialog for both deletes. `blocked` covers the case the server would refuse anyway —
// a template an assessment was created from — so the reason is shown before the click
// rather than as an error afterwards.
const ConfirmDeleteModal = ({ title, body, blocked, onConfirm, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const go = async () => {
    setBusy(true); setErr("");
    const res = await onConfirm();
    setBusy(false);
    if (res?.ok) onClose(); else setErr(res?.error || "Could not delete that.");
  };

  return (
    <Modal title={title} onClose={onClose}
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="danger" onClick={go} disabled={busy || blocked}>
          {busy ? "Deleting…" : "Delete"}
        </Button>
      </>}>
      {blocked ? <Alert tone="warning">{body}</Alert> : <p className="v2-tpl-card-desc">{body}</p>}
      <Alert>{err}</Alert>
    </Modal>
  );
};

export default ReviewTemplatesScreen;
